import { Box, Stack, Typography, Chip } from '@mui/material';
import { getFormattedDateUTC } from 'utils/getFormattedDate';
import { useTheme, alpha } from '@mui/material/styles';
import {
  UserOutlined,
  IdcardOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import VisibilityBadge from './UserViewBadge';
import { OwnerCandidate, OwnerPickerReason } from '../types';

interface Props {
  applicationId: string;
  applicationNumber: number;
  rawNeedle: string;
  candidates: OwnerCandidate[];
  reason?: OwnerPickerReason;
  resolved?: boolean;
  onPick: (ownerIndex: number) => void;
}

const titleFor = (reason: OwnerPickerReason, rawNeedle: string) => {
  if (reason === 'ambiguous') return `Multiple owners match "${rawNeedle}"`;
  return `Didn't find "${rawNeedle}" on this application`;
};

const subtitleFor = (reason: OwnerPickerReason, applicationNumber: number) => {
  if (reason === 'ambiguous') {
    return `Application #${applicationNumber} has more than one owner with this name. Pick one to continue.`;
  }
  return `Application #${applicationNumber} has these owners on file — pick one to analyze.`;
};

const formatDob = (dob: string | undefined): string | undefined => {
  if (!dob) return undefined;
  const iso = dob.includes('T') ? dob : `${dob}T00:00:00Z`;
  return getFormattedDateUTC(iso) || dob;
};

const OwnerPickerCard = ({
  applicationNumber,
  rawNeedle,
  candidates,
  reason = 'ambiguous',
  resolved,
  onPick,
}: Props) => {
  const theme = useTheme();
  const accent = theme.palette.info.main;

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.75,
        mb: 2,
        bgcolor: alpha(accent, 0.05),
        border: `1px solid ${alpha(accent, 0.22)}`,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.25, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(accent, 0.15),
        }}>
          <QuestionCircleOutlined style={{ fontSize: 17, color: accent }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: '0.9375rem',
              color: theme.palette.text.primary,
              mb: 0.5,
              lineHeight: 1.35,
            }}>
              {titleFor(reason, rawNeedle)}
            </Typography>
            <VisibilityBadge mode="hidden_from_ai" />
          </Stack>
          <Typography sx={{
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
            lineHeight: 1.55,
          }}>
            {subtitleFor(reason, applicationNumber)}
          </Typography>
        </Box>
      </Stack>

      {resolved ? (
        <Chip
          icon={<CheckCircleFilled style={{ fontSize: 11 }} />}
          label="Selected"
          size="small"
          sx={{
            mt: 1.5,
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            color: theme.palette.text.disabled,
            '& .MuiChip-icon': { color: theme.palette.text.disabled },
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ) : (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {candidates.map((c) => (
            <Box
              key={c.index}
              onClick={() => onPick(c.index)}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: accent,
                  bgcolor: alpha(accent, 0.06),
                  transform: 'translateX(2px)',
                },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{
                  width: 26, height: 26, borderRadius: 1, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(accent, 0.15),
                  color: accent,
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                }}>
                  {c.index}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <UserOutlined style={{ fontSize: 12, color: theme.palette.text.disabled }} />
                    <Typography sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                    }}>
                      {c.fullName}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
                    {c.dob && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CalendarOutlined style={{ fontSize: 11, color: theme.palette.text.disabled }} />
                        <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                          {formatDob(c.dob)}
                        </Typography>
                      </Stack>
                    )}
                    {c.ssnLast4 && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <IdcardOutlined style={{ fontSize: 11, color: theme.palette.text.disabled }} />
                        <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                          ••• •• {c.ssnLast4}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default OwnerPickerCard;
